import { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const GOOGLE_MAPS_SCRIPT_ID = "google-maps-places-script";
const GOOGLE_MAPS_CALLBACK = "__cashflow4uGooglePlacesReady";
let googlePlacesScriptPromise;

const loadGooglePlaces = (apiKey) => {
  if (window.google?.maps?.places) {
    return Promise.resolve(window.google);
  }

  if (googlePlacesScriptPromise) {
    return googlePlacesScriptPromise;
  }

  googlePlacesScriptPromise = new Promise((resolve, reject) => {
    const existingScript =
      document.getElementById(GOOGLE_MAPS_SCRIPT_ID) ||
      document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');

    const resolveWhenReady = () => {
      if (window.google?.maps?.places) {
        resolve(window.google);
        return;
      }

      reject(new Error("Google Places API did not load."));
    };

    if (existingScript) {
      if (window.google) {
        resolveWhenReady();
        return;
      }

      existingScript.addEventListener("load", resolveWhenReady, { once: true });
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Google Maps script failed to load.")),
        { once: true }
      );
      return;
    }

    window[GOOGLE_MAPS_CALLBACK] = resolveWhenReady;

    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src =
      "https://maps.googleapis.com/maps/api/js" +
      `?key=${encodeURIComponent(apiKey)}` +
      "&libraries=places" +
      `&callback=${GOOGLE_MAPS_CALLBACK}`;
    script.onerror = () => reject(new Error("Google Maps script failed to load."));

    document.head.appendChild(script);
  });

  return googlePlacesScriptPromise;
};

/**
 * Reusable Address Autocomplete Component
 * 
 * Provides Google Places address suggestions with:
 * - US-only address restrictions
 * - Structured address component parsing
 * - Callback with full address details including lat/lng
 * - Standard controlled input interface (value/onChange)
 */
const AddressAutocomplete = ({
  label = "Address",
  value = "",
  onChange,
  onPlaceSelected,
  placeholder = "Enter address",
  className = "",
  id,
  required = false,
  ...inputProps
}) => {
  const inputRef = useRef(null);
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [placesStatus, setPlacesStatus] = useState(apiKey ? "loading" : "unavailable");

  /**
   * Parse address components from Google Places result
   * Returns structured address data: street, city, state, zip, country, lat, lng, formatted_address
   */
  const parseAddressComponents = (place) => {
    if (!place?.address_components) {
      return null;
    }

    const components = {};
    const addressComponentMap = {
      street_number: "street_number",
      route: "route",
      locality: "city",
      administrative_area_level_1: "state",
      postal_code: "zip",
      country: "country",
    };

    place.address_components.forEach((component) => {
      const componentType = component.types[0];
      if (addressComponentMap[componentType]) {
        components[addressComponentMap[componentType]] = component.short_name;
      }
    });

    const result = {
      street: `${components.street_number || ""} ${components.route || ""}`.trim(),
      city: components.city || "",
      state: components.state || "",
      zip_code: components.zip || "",
      country: components.country || "US",
      lat: place.geometry?.location?.lat() || null,
      lng: place.geometry?.location?.lng() || null,
      formatted_address: place.formatted_address || "",
    };

    return result;
  };

  const handlePlaceSelected = (place) => {
    if (!place) return;

    const addressData = parseAddressComponents(place);

    // Update address field via onPlaceSelected only — avoids double state update
    if (onPlaceSelected && addressData) {
      onPlaceSelected({
        ...addressData,
        formatted_address: place.formatted_address || "",
      });
    } else if (onChange) {
      onChange({ target: { value: place.formatted_address || "" } });
    }
  };

  const inputClassName =
    "w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

  useEffect(() => {
    if (!apiKey || !inputRef.current) {
      setPlacesStatus("unavailable");
      return undefined;
    }

    let autocomplete;
    let placeChangedListener;
    let isMounted = true;

    setPlacesStatus("loading");

    loadGooglePlaces(apiKey)
      .then((google) => {
        if (!isMounted || !inputRef.current) return;

        autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
          types: ["address"],
          componentRestrictions: { country: "us" },
          fields: ["address_components", "geometry.location", "place_id", "formatted_address"],
        });

        placeChangedListener = autocomplete.addListener("place_changed", () => {
          handlePlaceSelected(autocomplete.getPlace());
        });

        setPlacesStatus("ready");
      })
      .catch((error) => {
        console.error("Address autocomplete unavailable:", error);
        if (isMounted) {
          setPlacesStatus("unavailable");
        }
      });

    return () => {
      isMounted = false;
      if (placeChangedListener) {
        placeChangedListener.remove();
      }
    };
    // Google Autocomplete owns the selected-place listener after setup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  return (
    <div className={className}>
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}
      <Input
        ref={inputRef}
        value={value}
        onChange={onChange}
        className={inputClassName}
        id={id}
        placeholder={placeholder}
        required={required}
        {...inputProps}
      />
      {placesStatus === "unavailable" && apiKey && (
        <p className="mt-1 text-xs text-muted-foreground">
          Address suggestions are unavailable right now. You can still enter the address manually.
        </p>
      )}
    </div>
  );
};

export default AddressAutocomplete;
